import { useEffect, useRef, useState } from 'react';
import { canvasWidth, groundOffsetY, groundSlope, groundYStart, msPerStep } from './constants';
import hikerFrames from './hikerFramesBase64';
import HitBox from './HitBox';

// Based on the backpacking icon, cropped to the figure's bounds (see hikerFramesBase64.ts).
const hikerHeight = 240;
const hikerWidth = 175;

const hitBoxFractions = [
  // Head
  { x: 0.4, y: 0, width: 0.3, height: 0.3 },
  // Body
  { x: 0.28, y: 0.28, width: 0.5, height: 0.6 }
];

// Walk cycle played as stride → passing → opposite stride → passing; values index into hikerFrames.
const walkFrameSequence = [0, 1, 2, 1];
const passingFrameIndex = 1;
// Each frame shows for half a "step" so the stride-to-stride cadence matches the original two-frame
// timing, with the passing frames smoothing the motion in between.
const msPerFrame = msPerStep / 2;
// The body rises slightly on the passing frame, like the lift at mid-stride of a real walk.
const passingBobOffset = -8;

const x = canvasWidth / 10;
const initialY = groundYStart - hikerHeight + groundOffsetY - x * groundSlope;

const jumpVelocity = 3.7;
const minJumpGravity = 0.015;
const maxJumpGravity = 0.035;
const maxJumpPressTime = 150;

const useHikerRef = (onComponentLoad: () => void, debugMode = false) => {
  const imgsRef = useRef<HTMLImageElement[]>([]);
  const [loadedImagesCount, setLoadedImagesCount] = useState(0);

  useEffect(() => {
    hikerFrames.forEach(src => {
      const image = new Image();
      image.src = src;
      image.onload = () => {
        setLoadedImagesCount(prev => prev + 1);
      };
      imgsRef.current.push(image);
    });
  }, []);

  useEffect(() => {
    if (loadedImagesCount === hikerFrames.length) {
      onComponentLoad();
    }
  }, [loadedImagesCount, onComponentLoad]);

  const hikerRef = useRef({
    walkMs: 0,
    y: initialY,
    isJumping: false,
    jumpingVelocity: 0,
    jumpingGravity: minJumpGravity,
    reset() {
      this.walkMs = 0;
      this.y = initialY;
      this.isJumping = false;
      this.jumpingVelocity = 0;
      this.jumpingGravity = minJumpGravity;
    },
    updateGravity(msDelta: number) {
      const elapsedTime = Math.min(msDelta, maxJumpPressTime);
      // Reduce the gravity the longer the space bar is pressed
      this.jumpingGravity = maxJumpGravity - (maxJumpGravity - minJumpGravity) * (elapsedTime / maxJumpPressTime);
    },
    jump() {
      if (!this.isJumping) {
        this.isJumping = true;
        this.jumpingVelocity = jumpVelocity;
        this.jumpingGravity = minJumpGravity;
      }
    },
    getHitBoxes(): HitBox[] {
      // Returns the hit boxes for the hiker in the form of [x, y, width, height]
      return hitBoxFractions.map(box => [x + box.x * hikerWidth, this.y + box.y * hikerHeight, box.width * hikerWidth, box.height * hikerHeight]);
    },
    draw(ctx: CanvasRenderingContext2D, msDelta: number) {
      const frames = imgsRef.current;

      if (this.isJumping) {
        this.jumpingVelocity -= this.jumpingGravity * msDelta;
        this.y = Math.min(initialY, this.y - this.jumpingVelocity * msDelta);
        if (this.y >= initialY) {
          this.y = initialY;
          this.isJumping = false;
        }
      }

      // Advance the walk cycle only while grounded so the frame holds (legs frozen) during a jump.
      if (!this.isJumping) {
        this.walkMs += msDelta;
      }
      // Cycle stride → passing → opposite stride → passing to animate the walk, and lift the body
      // slightly on the passing frame for a natural mid-stride bob.
      const frameIndex = walkFrameSequence[Math.floor(this.walkMs / msPerFrame) % walkFrameSequence.length];
      // Fall back to the first frame if the sequence ever references an index the loaded set doesn't
      // have, and bail before the game is loaded — drawImage throws a TypeError on a missing image.
      const frame = frames[frameIndex] ?? frames[0];
      if (!frame) {
        return;
      }
      const bob = !this.isJumping && frameIndex === passingFrameIndex ? passingBobOffset : 0;
      ctx.drawImage(frame, x, this.y + bob, hikerWidth, hikerHeight);

      if (debugMode) {
        ctx.save();
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'red';
        this.getHitBoxes().forEach(hitBox => {
          ctx.strokeRect(...hitBox);
        });
        ctx.restore();
      }
    }
  });

  return hikerRef;
};

export default useHikerRef;
